import React, { useState } from 'react';
import { Box, Button, Grid, Paper, Typography, Stack, IconButton } from '@mui/material';
import { DeleteOutline, History } from '@mui/icons-material';
import { COLORS } from '../../settings/themeSettings';
import './StandardCalculator.scss';

const StandardCalculator = () => {
    const [display, setDisplay] = useState('0');
    const [equation, setEquation] = useState('');
    const [history, setHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);

    const handleDigit = (digit) => {
        if (display === '0' || display === 'Error') {
            setDisplay(digit);
        } else {
            setDisplay(display + digit);
        }
    };

    const handleOperator = (op) => {
        if (display === 'Error') return;
        setEquation(display + ' ' + op + ' ');
        setDisplay('0');
    };

    const calculate = () => {
        try {
            const finalEquation = equation + display;
            // Utilisation d'une méthode plus sûre que eval pour une calculatrice simple
            // Pour une calculatrice standard, on peut utiliser Function ou un parseur
            // eslint-disable-next-line no-new-func
            const result = new Function('return ' + finalEquation.replace('×', '*').replace('÷', '/'))();

            const formattedResult = Number.isInteger(result) ? result.toString() : result.toFixed(2);

            setHistory([{ eq: finalEquation, res: formattedResult }, ...history].slice(0, 10));
            setDisplay(formattedResult);
            setEquation('');
        } catch (error) {
            setDisplay('Error');
        }
    };

    const clearAll = () => {
        setDisplay('0');
        setEquation('');
    };

    // eslint-disable-next-line no-unused-vars
    const backspace = () => {
        if (display.length > 1) {
            setDisplay(display.slice(0, -1));
        } else {
            setDisplay('0');
        }
    };

    const handlePercent = () => {
        setDisplay((parseFloat(display) / 100).toString());
    };

    const toggleSign = () => {
        setDisplay((parseFloat(display) * -1).toString());
    };

    const buttons = [
        { label: 'AC', action: clearAll, color: '#ff4d4d' },
        { label: '+/-', action: toggleSign, color: COLORS.primary },
        { label: '%', action: handlePercent, color: COLORS.primary },
        { label: '÷', action: () => handleOperator('÷'), color: COLORS.accent },

        { label: '7', action: () => handleDigit('7'), variant: 'digit' },
        { label: '8', action: () => handleDigit('8'), variant: 'digit' },
        { label: '9', action: () => handleDigit('9'), variant: 'digit' },
        { label: '×', action: () => handleOperator('×'), color: COLORS.accent },

        { label: '4', action: () => handleDigit('4'), variant: 'digit' },
        { label: '5', action: () => handleDigit('5'), variant: 'digit' },
        { label: '6', action: () => handleDigit('6'), variant: 'digit' },
        { label: '-', action: () => handleOperator('-'), color: COLORS.accent },

        { label: '1', action: () => handleDigit('1'), variant: 'digit' },
        { label: '2', action: () => handleDigit('2'), variant: 'digit' },
        { label: '3', action: () => handleDigit('3'), variant: 'digit' },
        { label: '+', action: () => handleOperator('+'), color: COLORS.accent },

        { label: '0', action: () => handleDigit('0'), variant: 'digit', sm: 6 },
        { label: '.', action: () => handleDigit('.'), variant: 'digit', sm: 3 },
        { label: '=', action: calculate, color: COLORS.success, sm: 3 },
    ];

    return (
        <Box className="standard-calculator-container">
            <Paper className="calculator-card" elevation={3}>
                <Box className="calculator-header">
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                        Calculatrice Standard
                    </Typography>
                    <IconButton size="small" onClick={() => setShowHistory(!showHistory)} sx={{ color: 'white' }}>
                        <History />
                    </IconButton>
                </Box>

                <Box className="display-area">
                    <Typography className="equation-text">
                        {equation}
                    </Typography>
                    <Typography className="display-text">
                        {display}
                    </Typography>
                </Box>

                {showHistory && (
                    <Box className="history-panel">
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                            <Typography variant="caption" sx={{ color: COLORS.secondary, fontWeight: 600 }}>HISTORIQUE</Typography>
                            <IconButton size="small" onClick={() => setHistory([])}><DeleteOutline fontSize="small" /></IconButton>
                        </Stack>
                        <Box sx={{ maxHeight: '150px', overflowY: 'auto' }}>
                            {history.map((item, idx) => (
                                <Box key={idx} className="history-item" onClick={() => setDisplay(item.res)}>
                                    <Typography variant="caption" sx={{ color: '#666' }}>{item.eq} =</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.res}</Typography>
                                </Box>
                            ))}
                            {history.length === 0 && <Typography variant="caption">Aucun historique</Typography>}
                        </Box>
                    </Box>
                )}

                <Grid container spacing={1.5} sx={{ p: 2 }}>
                    {buttons.map((btn, index) => (
                        <Grid item xs={btn.sm || 3} key={index}>
                            <Button
                                fullWidth
                                variant="contained"
                                className={`calc-btn ${btn.variant === 'digit' ? 'digit' : ''}`}
                                onClick={btn.action}
                                sx={{
                                    backgroundColor: btn.color || 'white',
                                    color: btn.color ? 'white' : COLORS.secondary,
                                    height: '50px',
                                    borderRadius: '12px',
                                    fontSize: '1.2rem',
                                    fontWeight: 600,
                                    boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                                    '&:hover': {
                                        backgroundColor: btn.color ? btn.color : '#f0f0f0',
                                        opacity: 0.9,
                                        transform: 'translateY(-2px)'
                                    },
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                {btn.label}
                            </Button>
                        </Grid>
                    ))}
                </Grid>
            </Paper>
        </Box>
    );
};

export default StandardCalculator;
